import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faExternalLinkAlt, faShareNodes, faChevronLeft, faChevronRight, faHome, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import { faGithub, faTwitter, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { theme } from '../theme';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const ProjectDetailsWrapper = styled.div`
  min-height: 100vh;
  background: ${theme.colors.neutral[50]};
  background-image: 
    radial-gradient(at 0% 0%, ${theme.colors.primary[50]} 0px, transparent 50%),
    radial-gradient(at 100% 100%, ${theme.colors.secondary[50]} 0px, transparent 50%);
`;

const ProjectHero = styled.div`
  padding: 8rem 2rem 3rem;
  background: linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.secondary[600]} 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 6rem 1.5rem 2rem;
  }

  @media (max-width: 480px) {
    padding: 5rem 1.25rem 1.5rem;
  }
`;

const ProjectDetailsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem 2rem;
  animation: ${fadeInUp} 0.6s ease-out;

  @media (max-width: 768px) {
    padding: 0 1.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 0 1.25rem 1.25rem;
  }
`;

const Breadcrumbs = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  font-size: ${theme.typography.fontSize.base};
  color: rgba(255, 255, 255, 0.95);
  flex-wrap: wrap;

  a {
    color: white;
    text-decoration: none;
    transition: all ${theme.transitions.base};
    padding: 0.5rem 0.75rem;
    border-radius: ${theme.borderRadius.md};
    font-weight: ${theme.typography.fontWeight.medium};
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    min-height: 44px;
    touch-action: manipulation;

  &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: translateX(-2px);
    }

    svg {
      font-size: 0.875rem;
    }
  }

  span {
    color: rgba(255, 255, 255, 0.5);
    font-weight: ${theme.typography.fontWeight.normal};
  }

  .current {
    color: rgba(255, 255, 255, 0.7);
    font-weight: ${theme.typography.fontWeight.normal};
  }

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.sm};
    margin-bottom: 1.25rem;
    gap: 0.375rem;

    a {
      padding: 0.5rem 0.75rem;
      font-size: ${theme.typography.fontSize.sm};
      min-height: 44px; /* Ensure good touch target */
    }
  }
`;

const TopActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.25rem;
    gap: 0.75rem;
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: white;
  text-decoration: none;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
  transition: all ${theme.transitions.base};
  box-shadow: ${theme.shadows.lg};

  &:hover {
    background: white;
    color: ${theme.colors.primary[600]};
    border-color: white;
    transform: translateX(-4px);
    box-shadow: ${theme.shadows.xl};
  }

  svg {
    transition: transform ${theme.transitions.base};
  }

  &:hover svg {
    transform: translateX(-4px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    min-height: 48px; /* Ensure good touch target */
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: ${theme.typography.fontSize.sm};
    min-height: 44px;
  }
`;

const ShareButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  
  &::before {
    content: 'Share:';
    color: rgba(255, 255, 255, 0.8);
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.medium};
    margin-right: 0.25rem;
  }

  @media (max-width: 768px) {
    &::before {
      content: '';
      margin-right: 0;
    }
  }
`;

const ShareButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.base};
  font-size: 0.875rem;
  box-shadow: ${theme.shadows.sm};
  min-height: 44px; /* Ensure good touch target */
  touch-action: manipulation;

  &:hover {
    background: white;
    color: ${theme.colors.primary[600]};
    border-color: white;
    transform: translateY(-3px) scale(1.1);
    box-shadow: ${theme.shadows.lg};
  }

  &:active {
    transform: translateY(-1px) scale(1.05);
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    min-height: 40px;
    font-size: 0.75rem;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const ProjectHeader = styled.div`
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out 0.1s both;
`;

const ProjectTitle = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-family: ${theme.typography.fontFamily.heading};
  font-weight: ${theme.typography.fontWeight.bold};
  color: white;
  margin-bottom: 1.5rem;
  line-height: ${theme.typography.lineHeight.tight};
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin-bottom: 1rem;
  }
`;

const ProjectMeta = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.95);
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.sm};
    gap: 0.375rem;
  }
`;

const ProjectBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.625rem 1.25rem;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.bold};
  box-shadow: ${theme.shadows.lg};
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @media (max-width: 480px) {
    padding: 0.5rem 1rem;
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const ProjectImage = styled.div`
  width: 100%;
  height: 550px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${theme.colors.neutral[100]};
  overflow: hidden;
  border-radius: ${theme.borderRadius['2xl']};
  margin-top: -6rem;
  margin-bottom: 4rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  border: 4px solid white;
  transition: transform ${theme.transitions.slow};
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.03) 100%);
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    height: 300px;
    margin-top: -4rem;
    border-width: 3px;
    margin-bottom: 3rem;
  }

  @media (max-width: 480px) {
    height: 250px;
    margin-top: -3rem;
    border-width: 2px;
    margin-bottom: 2rem;
    border-radius: ${theme.borderRadius.xl};
  }
`;

const ProjectContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  margin-bottom: 4rem;
  animation: ${fadeInUp} 0.6s ease-out 0.3s both;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: 480px) {
    gap: 1.5rem;
    margin-bottom: 3rem;
  }
`;

const ProjectDescription = styled.div`
  color: ${theme.colors.neutral[700]};
  line-height: ${theme.typography.lineHeight.relaxed};

  h2 {
    color: ${theme.colors.neutral[900]};
    font-family: ${theme.typography.fontFamily.heading};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize['2xl']};
    margin-bottom: 1.5rem;
    margin-top: 2.5rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    &:first-child {
      margin-top: 0;
    }

    &::before {
      content: '';
      width: 4px;
      height: 30px;
      background: ${theme.gradients.professional};
      border-radius: ${theme.borderRadius.full};
    }

    @media (max-width: 768px) {
      font-size: ${theme.typography.fontSize.xl};
    }
  }

  p {
    margin-bottom: 1.5rem;
    font-size: ${theme.typography.fontSize.base};

    @media (max-width: 480px) {
      font-size: ${theme.typography.fontSize.sm};
      margin-bottom: 1.25rem;
      line-height: ${theme.typography.lineHeight.relaxed};
    }
  }

  ul {
    list-style: none;
    padding-left: 0;

    li {
      position: relative;
      padding-left: 1.5rem;
      margin-bottom: 0.75rem;

      &::before {
        content: '▹';
        position: absolute;
        left: 0;
        color: ${theme.colors.primary[500]};
        font-weight: ${theme.typography.fontWeight.bold};
        font-size: ${theme.typography.fontSize.xl};
      }
    }
  }
`;

const ProjectSidebar = styled.div`
  background: white;
  padding: 2rem;
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.lg};
  border: 1px solid ${theme.colors.neutral[200]};
  height: fit-content;
  position: sticky;
  top: 120px;

  @media (max-width: 968px) {
    position: static;
  padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const TechStack = styled.div`
  margin-bottom: 2rem;

  h3 {
    color: ${theme.colors.neutral[900]};
    font-family: ${theme.typography.fontFamily.heading};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.lg};
    margin-bottom: 1rem;
  }
`;

const TechList = styled.ul`
  list-style: none;
  padding: 0;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const TechItem = styled.li`
  background: ${theme.colors.neutral[50]};
  padding: 0.625rem 1rem;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.neutral[700]};
  border: 1px solid ${theme.colors.neutral[200]};
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.primary[50]};
    color: ${theme.colors.primary[700]};
    border-color: ${theme.colors.primary[200]};
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSize.sm};
    padding: 0.5rem 0.875rem;
  }

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.xs};
    padding: 0.4rem 0.75rem;
  }
`;

const ProjectLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ProjectLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: ${theme.gradients.professional};
  color: white;
  text-decoration: none;
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
  transition: all ${theme.transitions.base};
  box-shadow: ${theme.shadows.md};
  position: relative;
  overflow: hidden;
  width: 100%;
  min-height: 48px; /* Ensure good touch target */
  touch-action: manipulation;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl};

    &::before {
      transform: translateX(100%);
    }
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 1.125rem;
  }

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: ${theme.typography.fontSize.base};
    min-height: 48px;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1.25rem;
    font-size: ${theme.typography.fontSize.sm};
    min-height: 44px;
  }
`;

const ProjectNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3rem 0;
  border-top: 2px solid ${theme.colors.neutral[200]};
  border-bottom: 2px solid ${theme.colors.neutral[200]};
  margin: 4rem 0;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
    padding: 2.5rem 0;
    margin: 3rem 0;
  }

  @media (max-width: 480px) {
    gap: 1.25rem;
    padding: 2rem 0;
    margin: 2.5rem 0;
  }
`;

const NavButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 2rem;
  background: white;
  border: 2px solid ${theme.colors.neutral[200]};
  border-radius: ${theme.borderRadius.xl};
  text-decoration: none;
  transition: all ${theme.transitions.base};
  flex: 1;
  max-width: 45%;
  box-shadow: ${theme.shadows.sm};

  &:hover {
    background: ${theme.gradients.professional};
    border-color: transparent;
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.xl};

    * {
      color: white !important;
    }

    svg {
      transform: translateX(${props => props.$direction === 'prev' ? '-4px' : '4px'});
    }
  }

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    padding: 1.25rem 1.5rem;
    min-height: 48px; /* Ensure good touch target */
  }

  @media (max-width: 480px) {
    padding: 1rem 1.25rem;
    gap: 0.75rem;
    min-height: 44px;
  }
`;

const NavContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

const NavLabel = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.neutral[500]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: ${theme.typography.fontWeight.semibold};
`;

const NavTitle = styled.span`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.neutral[900]};
  font-weight: ${theme.typography.fontWeight.semibold};
`;

const RelatedProjectsSection = styled.div`
  margin-top: 4rem;
  animation: ${fadeInUp} 0.6s ease-out 0.4s both;
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.fontSize['3xl']};
  font-family: ${theme.typography.fontFamily.heading};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.neutral[900]};
  margin-bottom: 2rem;
  text-align: center;

  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSize['2xl']};
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.xl};
    margin-bottom: 1.25rem;
  }
`;

const RelatedProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1.25rem;
  }
`;

const RelatedProjectCard = styled(Link)`
  background: white;
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
  text-decoration: none;
  border: 1px solid ${theme.colors.neutral[200]};
  transition: all ${theme.transitions.base};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.professional};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform ${theme.transitions.slow};
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${theme.shadows.xl};
    border-color: ${theme.colors.primary[300]};

    &::before {
      transform: scaleX(1);
    }
  }
`;

const RelatedProjectImage = styled.div`
  width: 100%;
  height: 200px;
  background-size: cover;
  background-position: center;
  background-color: ${theme.colors.neutral[100]};
`;

const RelatedProjectContent = styled.div`
  padding: 1.5rem;

  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const RelatedProjectTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.neutral[900]};
  margin-bottom: 0.5rem;

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.base};
  }
`;

const RelatedProjectDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.neutral[600]};
  line-height: ${theme.typography.lineHeight.relaxed};

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.xs};
  }
`;

const NotFoundContainer = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  animation: ${fadeInUp} 0.6s ease-out;

  h1 {
    font-size: ${theme.typography.fontSize['4xl']};
    font-family: ${theme.typography.fontFamily.heading};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.neutral[900]};
    margin-bottom: 1rem;
  }

  p {
    font-size: ${theme.typography.fontSize.lg};
    color: ${theme.colors.neutral[600]};
    margin-bottom: 2rem;
  }
`;

function ProjectDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [copiedLink, setCopiedLink] = useState(false);

  // Scroll to top when component mounts or ID changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant' // Use 'instant' for immediate scroll (no animation)
    });
  }, [id]); // Re-run when the project ID changes

  // Handle Back to Projects navigation
  const handleBackToProjects = (e) => {
    e.preventDefault();
    navigate('/');
    // Small delay to ensure navigation completes before scrolling
    setTimeout(() => {
      const projectsSection = document.getElementById('projects');
      if (projectsSection) {
        const headerOffset = 100;
        const elementPosition = projectsSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Share functionality
  const handleShare = (platform) => {
    const url = window.location.href;
    const title = project?.title || 'Check out this project';
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      copy: url
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  // Get project data based on ID
  const projects = [
    {
      id: 1,
      title: 'AlbaniTM Application',
      category: 'Mobile',
      year: '2024',
      description: 'A cross-platform mobile application for tracking and managing the electric motor of an innovative skateboard, built with Flutter.',
      longDescription: `The Albanitm Application is a comprehensive mobile solution designed for the "Albanitm" innovative skateboard. This application allows users to monitor and control various aspects of the electric motor, enhancing the overall user experience. It features real-time data display, control functionalities, and a user-friendly interface.
                        The project aims to bridge the gap between hardware (the skateboard's electric motor) and software, providing a seamless interaction for the user. It represents a significant step in developing integrated smart mobility solutions.`,
      image: '/images/albani_detailes.png',
      imageUrl: '/images/albani_detailes.png',
      techStack: ['Flutter', 'Dart', 'Firebase', 'REST APIs', 'State Management', 'Mobile UI/UX'],
      features: [
        'Responsive design for all devices',
        'Real-time data synchronization',
        'User authentication and authorization',
        'RESTful API integration'
      ],
      demo: '/images/albani_detailes.png',
      github: 'https://github.com/kabilmbarki/Albanitm_application'
    },
    {
      id: 2,
      category: 'Embedded',
      year: '2024',
      title: 'ESP32-CAM Face Detection & Emotion Analysis',
      description: 'An embedded system that captures facial images via HTTP, integrated with a separate module for real-time emotion recognition.',
      longDescription: `This project showcases a robust two-part system for real-time human emotion analysis. The first part, residing on an **ESP32-CAM** microcontroller, is responsible for **real-time face detection** within its video stream. When a face is detected, it frames the video to capture a still image and publishes this image via **HTTP**.

  The second part involves a separate **processing module** a Python application running on a  PC. This module receives the images via HTTP from the ESP32-CAM and then applies advanced image processing and machine learning techniques to **analyze facial expressions and determine the user's emotion**.

  This architecture allows for efficient resource allocation, with the ESP32-CAM handling lightweight image capture and pre-processing, and a more powerful system performing the computationally intensive emotion recognition. It's a versatile foundation for applications requiring remote visual analysis, such as smart surveillance, interactive displays, or health monitoring.`,
      image: '/images/face_emotion/project_details.png',
      imageUrl: '/images/face_emotion/project_details.png',
      techStack: ['ESP32-CAM', 'Python', 'Machine Learning', 'Image Processing','Embedded C/C++',
    'HTTP/TCP/IP','OpenCV',],
      features: [
        'Real-time human face detection on ESP32-CAM',
    'Image capture and HTTP publishing from ESP32-CAM',
    'External module for emotion recognition from received images',
    'Efficient resource utilization via distributed processing',
    'Scalable for various monitoring and interactive applications',
    'Foundation for AI-powered embedded vision systems'
      ],
      demo: 'https://github.com/kabilmbarki/Face-emotion-with-ESP32CAM/tree/main/Real_Demo',
      github: 'https://github.com/kabilmbarki/Face-emotion-with-ESP32CAM'
    },
    {
      id: 3,
      category: 'IoT',
      year: '2025',
      title: 'Planty - Smart Agriculture Monitoring System',
      description: 'An IoT-based system for smart agriculture, monitoring plant health and automating care, recognized in a university competition.',
      longDescription:`**Planty** is an innovative IoT project designed to revolutionize agriculture by automating plant care and providing real-time environmental insights. Developed in collaboration with **Sarah Dhouib**, this system utilizes an ESP32 microcontroller to monitor critical parameters such as soil moisture, temperature, and humidity.

  The core functionality includes:
  * **Real-time Data Monitoring:** Sensors continuously gather environmental data, which is then sent to a cloud platform (Blynk app) for live visualization and analysis.
  * **Automated Watering System:** Based on predefined thresholds for soil moisture, the system can automatically activate a water pump, ensuring optimal hydration for plants and efficient water usage.
  * **Alerts and Notifications:** The system provides alerts, for example, via a buzzer for low water levels, enabling timely intervention.

  This project was successfully developed and tested, demonstrating high efficiency and accuracy in data acquisition and automated response. It was a proud participant in the University of Monastir's "Les Meilleures Initiatives Innovantes et Entrepreneuriales" competition, where it achieved the **4th place** among all university projects. This recognition underscores its practical applicability and innovative approach to addressing challenges in smart agriculture.`,
      image: '/images/planty/planty_details1.png',
      imageUrl: '/images/planty/planty_details1.png', 
      techStack: ['ESP32',
    'Arduino IDE',
    'C++', // For ESP32 programming
    'IoT',
    'DHT11 Sensor',
    'Soil Moisture Sensor',
    'Water Pump'],
      features: [
        'Automated plant care with ESP32 microcontroller',
    'Monitors soil moisture, temperature, and humidity',
    'Automated watering system based on sensor data',
    'Real-time monitoring and control via Blynk app',
    'Alert system (e.g., buzzer for low water levels)',
    'Efficient resource management for agriculture',
    'Recognized with 4th place in "Les Meilleures Initiatives Innovantes et Entrepreneuriales" competition'
  ],
      demo: 'https://www.linkedin.com/posts/kabil-mbarki_smartagriculture-iot-agritech-activity-7344794214150418435-oivr?utm_source=share&utm_medium=member_desktop&rcm=ACoAAD4bRLEBNTLeipAFp5dhg5Y5I78_Qffm3RA',
      github: 'https://github.com/kabilmbarki/Planty---Smart-Agriculture-Monitoring-System'
    },
    {
      id: 4,
      category: 'Mobile',
      year: '2024',
      title: 'Smart Inventory Management System',
      description: 'A comprehensive Flutter application for efficient stock management, integrated with QR codes and a Flask backend.',
      longDescription: `This **Smart Inventory Management System** is a robust application designed to offer intuitive and efficient control over stock. It features a cross-platform frontend built with **Flutter**, providing a seamless user experience, and is powered by a **Flask** backend for data management.
    
      The system is engineered to streamline inventory operations, enabling real-time tracking of articles, efficient management of departments and physical locations, and comprehensive inventory functionalities. A key innovation is the integration of **QR code generation and scanning** for rapid and accurate article tracking. The dashboard provides clear data visualizations and statistics, while supporting both light and dark themes for user preference.`,
      image: '/images/inventaire/inventaire.png', // Ensure you have a relevant image in your public/images folder
      imageUrl: '/images/inventaire/inventaire.png', // Same as above
      techStack: [
        'Flutter',
        'Dart',
        'Flask',
        'Python',
        'SQLite',
        'RESTful API',
        'QR Code Scanner',
        'QR Code Generation',
        'Provider (State Management)',
        'Charts (fl_chart, charts_flutter)'
      ],
      features: [
        'User authentication (Login/Registration)',
        'Article (product) management (Add, Modify, Track)',
        'Department and Room management for organization',
        'Comprehensive inventory functionalities',
        'Dashboard with data visualization and statistics',
        'QR code generation and scanning for articles',
        'Light/Dark theme support'
      ],
      demo: 'https://github.com/kabilmbarki/Gestion-de-Stock---Syst-me-d-Inventaire-Intelligent/tree/main/Demo', // Replace with live demo URL if available (e.g., video demo or app store link)
      github: 'https://github.com/kabilmbarki/Gestion-de-Stock---Syst-me-d-Inventaire-Intelligent'
    },
    {
      id: 6,
      category: 'Web',
      year: '2024',
      title: 'Le Saphir Bleu Café Website',
      description: 'A modern React web application for "Le Saphir Bleu Café," featuring an intuitive menu and responsive design.',
      longDescription: `**Le Saphir Bleu Café** is a contemporary web application built with **React** designed to showcase the café's menu and offerings. It provides customers with an intuitive way to explore various categories of dishes including Food, Desserts, Beverages, and Shisha. The application emphasizes a reactive and modern user interface, ensuring a seamless experience across all devices, including mobile.`,
      image: '/images/lesahirblue_cafe/lesaphirbleu.png', // You'll need to add a suitable image for this project to your /public/images folder, perhaps the logo or a screenshot of the menu.
      imageUrl: '/images/lesahirblue_cafe/lesaphirbleu.png', // Same as above
      techStack: [
        'React 19',
        'React Router',
        'React Bootstrap',
        'CSS Modules',
        'React Icons',
        'JavaScript',
        'HTML',
        'CSS'
      ],
      features: [
        'Intuitive navigation between menu sections',
        'Categorized display of dishes (Food, Desserts, Drinks, Shisha)',
        'Responsive and modern user interface',
        'Mobile-adapted design'
      ],
      demo: 'https://le-saphir-bleu.netlify.app', // If you have a live demo hosted (e.g., on Netlify/Vercel), replace '#' with the URL.
      github: 'https://github.com/kabilmbarki/Le-Saphir-Bleu'
    },
    {
      id: 5,
      category: 'IoT',
      year: '2024',
      title: 'Smart Home Control App',
      description: 'A comprehensive Flutter application for complete home automation, environmental monitoring, and smart notifications, integrated with Firebase.',
      longDescription: `The **Smart Home Control App** is a versatile and robust mobile application built with **Flutter** that provides full home automation and intelligent monitoring capabilities. It integrates seamlessly with **Firebase** for secure user authentication, real-time data synchronization via Realtime Database, and push notifications through Cloud Messaging.
    
      This application empowers users to manage various aspects of their smart environment, including:
      * **Home Automation**: Control lighting (indoor/outdoor), manage water systems, oversee garden irrigation, and even care for farm animals.
      * **Environmental Monitoring**: Track real-time temperature, humidity, and gas levels across multiple zones (Home, Garden, Farm).
      * **Smart Notifications**: Receive real-time alerts for abnormal conditions and system status updates based on custom thresholds.
    
      The app boasts a responsive and intuitive user interface, ensuring a beautiful experience across various platforms including Android, iOS, Web, Windows, macOS, and Linux. Its secure user authentication ensures a personalized and protected smart home experience.`,
      image: '/images/homeapp/lunchicon.png',
      imageUrl: '/images/homeapp/lunchicon.png',
      techStack: [
        'Flutter',
        'Dart',
        'Firebase (Authentication, Realtime Database)',
        'Provider (State Management)',
        'HTTP Requests',
        'Local Notifications',
        'Cross-Platform Development',
        'IoT Integration'
      ],
      features: [
        'Comprehensive home automation control (lights, water, garden, farm animals)',
        'Real-time multi-zone environmental monitoring (temp, humidity, gas)',
        'Smart notifications and threshold-based alerts',
        'Secure user authentication and account creation (Firebase)',
        'Cross-platform compatibility (Android, iOS)',
        'Responsive and intuitive UI'
      ],
      demo: 'https://github.com/kabilmbarki/Smart-Home-Control-App/tree/main/demo', // Replace with a video demo link, app store link, or hosted web demo if available.
      github: 'https://github.com/kabilmbarki/Smart-Home-Control-App'
    },
    {
      id: 7,
      category: 'Embedded',
      year: '2025',
      title: 'Arduino Dual-Axis Solar Tracker',
      description: 'An Arduino-based solar tracker that automatically adjusts its position for maximum solar panel efficiency using LDRs and servo motors.',
      longDescription: `The **Arduino Dual-Axis Solar Tracker** is an innovative project designed to significantly enhance the efficiency of solar panels. It achieves this by automatically adjusting the solar panel's orientation to continuously follow the sun's movement throughout the day.
    
      The system operates using **four Light Dependent Resistors (LDRs)** to detect variations in light intensity. Based on these readings, an **Arduino Nano** microcontroller calculates the direction of maximum sunlight. Two **servo motors** then precisely adjust the solar panel's position along both horizontal and vertical axes to maximize light exposure. The structural components of the tracker are custom-designed and **3D printed**, ensuring a compact and efficient build. This project demonstrates practical application of embedded systems, sensor integration, and automated control for renewable energy solutions.`,
      image: '/images/solartracker/solartracker.png', // You'll need to add a suitable image for this project to your /public/images folder. A screenshot of the app's dashboard or a key control screen would be ideal.
      imageUrl: '/images/solartracker/solartracker.png',// Same as above
      techStack: [
        'Arduino Nano',
        'C/C++', // For Arduino programming
        'LDRs',
        'Servo Motors',
        '3D Printing',
        'Embedded Systems',
        'Sensor Integration'
      ],
      features: [
        'Dual-axis sun tracking (horizontal and vertical)',
        'Automated adjustment to maximize light intensity',
        'Utilizes 4 LDRs for precise light detection',
        'Controlled by Arduino Nano microcontroller',
        'Customizable 3D printed structural parts',
        'Configurable delay and light intensity tolerance'
      ],
      demo: 'https://www.linkedin.com/posts/kabil-mbarki_solarenergy-renewableenergy-electronics-activity-7347674800519045121-b1dr?utm_source=share&utm_medium=member_desktop&rcm=ACoAAD4bRLEBNTLeipAFp5dhg5Y5I78_Qffm3RA', // Consider linking to a video demonstration of the tracker in action if available.
      github: 'https://github.com/kabilmbarki/Solar-Tracker-Project'
    }
  ];

  const project = projects.find(p => p.id === parseInt(id));
  
  // Get adjacent projects for navigation
  const currentIndex = projects.findIndex(p => p.id === parseInt(id));
  const prevProject = currentIndex > 0 ? projects[currentIndex - 1] : null;
  const nextProject = currentIndex < projects.length - 1 ? projects[currentIndex + 1] : null;
  
  // Get related projects (same category, excluding current)
  const relatedProjects = project 
    ? projects.filter(p => p.category === project.category && p.id !== project.id).slice(0, 3)
    : [];

  if (!project) {
    const handleBackToHome = (e) => {
      e.preventDefault();
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    };

    return (
      <ProjectDetailsWrapper>
        <ProjectDetailsContainer>
          <NotFoundContainer>
            <h1>😕 Project Not Found</h1>
            <p>Sorry, we couldn't find the project you're looking for.</p>
            <BackButton to="/" onClick={handleBackToHome}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Home
            </BackButton>
          </NotFoundContainer>
        </ProjectDetailsContainer>
      </ProjectDetailsWrapper>
    );
  }

  return (
    <ProjectDetailsWrapper>
      {/* Hero Section with Gradient Background */}
      <ProjectHero>
        <HeroContent>
          {/* Breadcrumbs */}
          <Breadcrumbs>
            <Link to="/">
              <FontAwesomeIcon icon={faHome} />
              Home
            </Link>
            <span>/</span>
            <Link to="/" onClick={handleBackToProjects}>
              <FontAwesomeIcon icon={faFolderOpen} />
              Projects
            </Link>
            <span>/</span>
            <span className="current">{project.title}</span>
          </Breadcrumbs>

          {/* Share Buttons */}
          <TopActions>
            <ShareButtons>
              <ShareButton 
                onClick={() => handleShare('twitter')}
                title="Share on Twitter"
              >
                <FontAwesomeIcon icon={faTwitter} />
              </ShareButton>
              <ShareButton 
                onClick={() => handleShare('linkedin')}
                title="Share on LinkedIn"
              >
                <FontAwesomeIcon icon={faLinkedin} />
              </ShareButton>
              <ShareButton 
                onClick={() => handleShare('copy')}
                title={copiedLink ? 'Link Copied!' : 'Copy Link'}
              >
                <FontAwesomeIcon icon={faShareNodes} />
              </ShareButton>
            </ShareButtons>
          </TopActions>

          {/* Project Header */}
        <ProjectHeader>
          <ProjectTitle>{project.title}</ProjectTitle>
            <ProjectMeta>
              <ProjectBadge>{project.category}</ProjectBadge>
              <MetaItem>📅 {project.year}</MetaItem>
            </ProjectMeta>
        </ProjectHeader>
        </HeroContent>
      </ProjectHero>

      <ProjectDetailsContainer>
        {/* Project Image */}
        <ProjectImage style={{ backgroundImage: `url(${project.image})` }} />

        {/* Main Content */}
        <ProjectContent>
          <ProjectDescription>
            <h2>Overview</h2>
            <p>{project.description}</p>
            <h2>Details</h2>
            <p>{project.longDescription}</p>
            <h2>Key Features</h2>
            <ul>
              {project.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </ProjectDescription>

          <ProjectSidebar>
            <TechStack>
              <h3>Technologies Used</h3>
              <TechList>
                {project.techStack.map((tech, index) => (
                  <TechItem key={index}>{tech}</TechItem>
                ))}
              </TechList>
            </TechStack>

            <ProjectLinks>
              {project.demo && project.demo !== '#' && (
                <ProjectLink href={project.demo} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faExternalLinkAlt} /> Live Demo
                </ProjectLink>
              )}
              {project.github && project.github !== '#' && (
                <ProjectLink href={project.github} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faGithub} /> GitHub
                </ProjectLink>
              )}
            </ProjectLinks>
          </ProjectSidebar>
        </ProjectContent>

        {/* Project Navigation */}
        {(prevProject || nextProject) && (
          <ProjectNavigation>
            {prevProject ? (
              <NavButton to={`/project/${prevProject.id}`} $direction="prev">
                <FontAwesomeIcon icon={faChevronLeft} size="2x" style={{ color: theme.colors.neutral[600] }} />
                <NavContent>
                  <NavLabel>Previous Project</NavLabel>
                  <NavTitle>{prevProject.title}</NavTitle>
                </NavContent>
              </NavButton>
            ) : <div />}

            {nextProject && (
              <NavButton to={`/project/${nextProject.id}`} $direction="next" style={{ textAlign: 'right' }}>
                <NavContent>
                  <NavLabel>Next Project</NavLabel>
                  <NavTitle>{nextProject.title}</NavTitle>
                </NavContent>
                <FontAwesomeIcon icon={faChevronRight} size="2x" style={{ color: theme.colors.neutral[600] }} />
              </NavButton>
            )}
          </ProjectNavigation>
        )}

        {/* Related Projects */}
        {relatedProjects.length > 0 && (
          <RelatedProjectsSection>
            <SectionTitle>Related Projects</SectionTitle>
            <RelatedProjectsGrid>
              {relatedProjects.map((relProject) => (
                <RelatedProjectCard key={relProject.id} to={`/project/${relProject.id}`}>
                  <RelatedProjectImage style={{ backgroundImage: `url(${relProject.image})` }} />
                  <RelatedProjectContent>
                    <RelatedProjectTitle>{relProject.title}</RelatedProjectTitle>
                    <RelatedProjectDescription>{relProject.description}</RelatedProjectDescription>
                  </RelatedProjectContent>
                </RelatedProjectCard>
              ))}
            </RelatedProjectsGrid>
          </RelatedProjectsSection>
        )}
      </ProjectDetailsContainer>
    </ProjectDetailsWrapper>
  );
}

export default ProjectDetails;
